// Roles, permissions and record grants (PLAN.md 4.3 and 4.4). Pure: no database, no clock.

import { DEFAULT_TIME_ZONE, zonedDay } from "./week";

/** Every area of FlowCare OS a role sets, from the mockup's menus. */
export const AREAS = [
  "patients",
  "visits",
  "clients",
  "candidates",
  "workers",
  "trips",
  "fleet",
  "billing",
  "payroll",
  "business",
  "compliance",
  "settings",
  "inbox",
] as const;
export type Area = (typeof AREAS)[number];

export const LEVELS = ["none", "view", "edit"] as const;
export type Level = (typeof LEVELS)[number];

/** Switches for sensitive actions, each kept on the row of the area it belongs to. */
export const SWITCH_AREA = {
  approve_payroll: "payroll",
  edit_pay_rates: "payroll",
  void_invoice: "billing",
  release_block: "billing",
  view_audit: "settings",
  manage_users: "settings",
  // Saving a file to the computer (brief 1B-9): files open in the viewer; downloading is a switch.
  download_files: "settings",
  see_background_results: "candidates",
  // The Money switch (brief 1D-5): pay, billing and margin show only to roles with it.
  see_money: "billing",
} as const satisfies Record<string, Area>;
export type Switch = keyof typeof SWITCH_AREA;
export const SWITCHES = Object.keys(SWITCH_AREA) as Switch[];

/** Plain-English names, as the screens and the history show them. */
export const AREA_LABEL: Record<Area, string> = {
  patients: "Patients",
  visits: "Visits",
  clients: "Clients",
  candidates: "Candidates",
  workers: "Workers",
  trips: "Trips",
  fleet: "Fleet",
  billing: "Billing",
  payroll: "Payroll",
  business: "Business",
  compliance: "Compliance",
  settings: "Settings",
  inbox: "Inbox",
};

export const SWITCH_LABEL: Record<Switch, string> = {
  approve_payroll: "Approve payroll",
  edit_pay_rates: "Edit pay rates",
  void_invoice: "Void an invoice",
  release_block: "Release a block",
  view_audit: "View audit",
  manage_users: "Manage users and roles",
  download_files: "Download files",
  see_background_results: "See background and drug results",
  see_money: "Sees financials",
};

export const ADMIN_ROLE = "Admin";

/** One row of role_permissions, as the rules see it. */
export interface PermissionRow {
  area: Area;
  level: Level;
  own_only: boolean;
  switches: Partial<Record<Switch, boolean>>;
}

/** A role and its rows. A missing area row means none. */
export interface RoleGrant {
  name: string;
  built_in: boolean;
  permissions: readonly PermissionRow[];
}

/** How far someone reaches in one area at one level: nowhere, their own records, or all. */
export type Reach = "none" | "own" | "all";

export interface AreaAccess {
  view: Reach;
  edit: Reach;
}

/** What a person may do, worked out from all their roles. */
export interface Access {
  admin: boolean;
  areas: Record<Area, AreaAccess>;
  switches: Record<Switch, boolean>;
}

const REACH_ORDER: Record<Reach, number> = { none: 0, own: 1, all: 2 };
const wider = (a: Reach, b: Reach): Reach => (REACH_ORDER[a] >= REACH_ORDER[b] ? a : b);

export function isAdminRole(role: { name: string; built_in: boolean }): boolean {
  return role.built_in && role.name === ADMIN_ROLE;
}

function fill<K extends string, V>(keys: readonly K[], value: (k: K) => V): Record<K, V> {
  return Object.fromEntries(keys.map((k) => [k, value(k)])) as Record<K, V>;
}

/** Nothing at all: someone with no roles, such as an outside portal user. */
export function noAccess(): Access {
  return {
    admin: false,
    areas: fill(AREAS, () => ({ view: "none", edit: "none" })),
    switches: fill(SWITCHES, () => false),
  };
}

/** Admin is fixed: every area at edit, every switch on, whatever its rows say. */
export function adminAccess(): Access {
  return {
    admin: true,
    areas: fill(AREAS, () => ({ view: "all", edit: "all" })),
    switches: fill(SWITCHES, () => true),
  };
}

/**
 * Someone with several roles gets the most any of them allows: for each area and level the
 * widest reach any role gives (all beats only their own), and every switch any role turns on.
 */
export function effectiveAccess(roles: readonly RoleGrant[]): Access {
  if (roles.some(isAdminRole)) return adminAccess();
  const access = noAccess();
  for (const role of roles) {
    for (const row of role.permissions) {
      const a = access.areas[row.area];
      if (!a) continue;
      const reach: Reach = row.own_only ? "own" : "all";
      if (row.level === "view" || row.level === "edit") a.view = wider(a.view, reach);
      if (row.level === "edit") a.edit = wider(a.edit, reach);
      for (const s of SWITCHES) {
        if (row.switches[s] === true && SWITCH_AREA[s] === row.area) access.switches[s] = true;
      }
    }
  }
  return access;
}

/** Whether someone reaches an area at a level at all (their own records or more). */
export function allows(access: Access, area: Area, level: Exclude<Level, "none">): boolean {
  return access.areas[area][level] !== "none";
}

/**
 * A patient's Messages card (brief 1E-34, control tower on PR #68): office staff who see the patient.
 * Someone who reaches patients only as a caregiver does (their own, to view, never to change) is
 * refused; what's about her visits is in her app's Chat.
 */
export function seesPatientMessages(access: Access): boolean {
  return !(access.areas.patients.view !== "all" && access.areas.patients.edit === "none");
}

export function allowsSwitch(access: Access, name: Switch): boolean {
  return access.switches[name];
}

/** A record as "only their own" sees it: the users it is assigned to. */
export interface Assigned {
  assigned_to: readonly string[];
}

/** "Only their own" narrows to records the person is assigned to; "all" reaches every record. */
export function reaches(
  access: Access,
  area: Area,
  level: Exclude<Level, "none">,
  record: Assigned,
  userId: string,
): boolean {
  const reach = access.areas[area][level];
  if (reach === "all") return true;
  return reach === "own" && record.assigned_to.includes(userId);
}

/** The records of a list someone may see at a level. */
export function narrow<R extends Assigned>(
  access: Access,
  area: Area,
  level: Exclude<Level, "none">,
  records: readonly R[],
  userId: string,
): R[] {
  return records.filter((r) => reaches(access, area, level, r, userId));
}

/**
 * Whether one access gives anything another doesn't. Someone who manages users but isn't an
 * Admin can't hand out more than they have themselves.
 */
export function exceeds(wanted: Access, held: Access): boolean {
  if (held.admin) return false;
  if (wanted.admin) return true;
  for (const area of AREAS) {
    for (const level of ["view", "edit"] as const) {
      if (REACH_ORDER[wanted.areas[area][level]] > REACH_ORDER[held.areas[area][level]])
        return true;
    }
  }
  return SWITCHES.some((s) => wanted.switches[s] && !held.switches[s]);
}

// ---- Editing roles ---------------------------------------------------------------------------

/** Admin can't be edited or removed. Returns why a change to this role is refused, or null. */
export function refuseRoleEdit(role: { name: string; built_in: boolean }): string | null {
  return isAdminRole(role) ? "Admin can't be edited or removed" : null;
}

/**
 * Reads a role's new permissions from a request body: an array of {area, level, own_only,
 * switches}. Each area at most once; a switch only on its own area's row; "only their own"
 * needs a level. Returns the rows (one per area, missing areas as none) or why they're refused.
 */
export function readPermissions(
  input: unknown,
): { ok: true; rows: PermissionRow[] } | { ok: false; error: string } {
  if (!Array.isArray(input)) return { ok: false, error: "permissions must be a list" };
  const byArea = new Map<Area, PermissionRow>();
  for (const raw of input as unknown[]) {
    if (typeof raw !== "object" || raw === null) return { ok: false, error: "bad permission" };
    const r = raw as Record<string, unknown>;
    const area = r.area as Area;
    if (!AREAS.includes(area)) return { ok: false, error: `unknown area ${String(r.area)}` };
    if (byArea.has(area)) return { ok: false, error: `${area} is listed twice` };
    const level = r.level as Level;
    if (!LEVELS.includes(level)) return { ok: false, error: `unknown level for ${area}` };
    const ownOnly = r.own_only ?? false;
    if (typeof ownOnly !== "boolean") return { ok: false, error: `own_only for ${area}` };
    if (ownOnly && level === "none")
      return { ok: false, error: `${area}: only their own needs view or edit` };
    const rawSwitches = r.switches ?? {};
    if (typeof rawSwitches !== "object" || rawSwitches === null || Array.isArray(rawSwitches))
      return { ok: false, error: `switches for ${area}` };
    const switches: Partial<Record<Switch, boolean>> = {};
    for (const [name, on] of Object.entries(rawSwitches)) {
      if (!(name in SWITCH_AREA)) return { ok: false, error: `unknown switch ${name}` };
      if (SWITCH_AREA[name as Switch] !== area)
        return { ok: false, error: `${name} belongs to ${SWITCH_AREA[name as Switch]}` };
      if (typeof on !== "boolean") return { ok: false, error: `${name} must be true or false` };
      if (on) switches[name as Switch] = true;
    }
    byArea.set(area, { area, level, own_only: ownOnly, switches });
  }
  return {
    ok: true,
    rows: AREAS.map(
      (area) => byArea.get(area) ?? { area, level: "none", own_only: false, switches: {} },
    ),
  };
}

/**
 * The last active Admin can't be switched off or demoted. `adminsAfter` is how many active
 * Admins the company would have after the change; `hadAdmin` whether it had one before.
 */
export function refuseAdminLoss(hadAdmin: boolean, adminsAfter: number): string | null {
  return hadAdmin && adminsAfter < 1
    ? "the last active Admin can't be switched off or demoted"
    : null;
}

// ---- Built-in roles --------------------------------------------------------------------------

type Spec = Partial<Record<Area, `${"view" | "edit"}${"" | " own"}`>>;

function rows(spec: Spec, switches: Switch[] = []): PermissionRow[] {
  return AREAS.map((area) => {
    const s = spec[area];
    const on = switches.filter((sw) => SWITCH_AREA[sw] === area);
    return {
      area,
      level: s ? (s.split(" ")[0] as Level) : "none",
      own_only: s?.endsWith(" own") ?? false,
      switches: Object.fromEntries(on.map((sw) => [sw, true])),
    };
  });
}

/** Every new company gets these. Admin is fixed; the others can be changed by the company. */
export const BUILT_IN_ROLES: readonly { name: string; permissions: PermissionRow[] }[] = [
  {
    name: ADMIN_ROLE,
    permissions: rows(Object.fromEntries(AREAS.map((a) => [a, "edit"])) as Spec, SWITCHES),
  },
  {
    name: "Office",
    permissions: rows({
      patients: "edit",
      visits: "edit",
      clients: "edit",
      candidates: "edit",
      workers: "edit",
      trips: "edit",
      fleet: "view",
      billing: "view",
      settings: "view",
      inbox: "edit",
    }),
  },
  {
    name: "Scheduler",
    permissions: rows({
      patients: "view",
      visits: "edit",
      clients: "view",
      workers: "view",
      trips: "edit",
      fleet: "view",
      inbox: "edit",
    }),
  },
  {
    name: "Billing",
    permissions: rows(
      {
        patients: "view",
        visits: "view",
        clients: "view",
        trips: "view",
        billing: "edit",
        payroll: "view",
        inbox: "edit",
      },
      ["void_invoice", "release_block", "see_money"],
    ),
  },
  {
    name: "Payroll",
    permissions: rows(
      { visits: "view", workers: "view", trips: "view", payroll: "edit", inbox: "edit" },
      // Payroll sees pay (brief 1D-6): the Money switch, on the billing row like every role's.
      ["approve_payroll", "edit_pay_rates", "see_money"],
    ),
  },
  {
    name: "Recruiter",
    permissions: rows({ clients: "view", candidates: "edit", workers: "view", inbox: "edit" }),
  },
  {
    name: "Credentialing",
    permissions: rows({ candidates: "edit", workers: "edit", inbox: "edit" }, [
      "see_background_results",
    ]),
  },
  {
    name: "Clinical supervisor (RN)",
    permissions: rows({
      patients: "edit own",
      visits: "edit own",
      workers: "view",
      inbox: "edit",
    }),
  },
  { name: "Caregiver", permissions: rows({ patients: "view own", visits: "edit own" }) },
  { name: "Driver", permissions: rows({ trips: "edit own", fleet: "view" }) },
];

// ---- Record grants ---------------------------------------------------------------------------

/**
 * What a grant can list for each kind of record (the mockup's "What they can see"). "files" lets
 * an outside person open the record's files (brief 1A-7).
 */
export const GRANT_CAN_SEE = {
  patient: ["visits", "visit_notes", "care_plan", "invoices", "messages", "files"],
  job: ["job_details", "their_application", "messages"],
  unit: ["profiles_sent", "timesheets", "invoices"],
  trip: ["trip_times", "invoices", "files"],
} as const;
export type RecordKind = keyof typeof GRANT_CAN_SEE;
export const RECORD_KINDS = Object.keys(GRANT_CAN_SEE) as RecordKind[];

export interface Grant {
  record_kind: string;
  record_id: string;
  can_see: readonly string[];
  /** Last day of access, YYYY-MM-DD in New York. */
  ends_on: string;
}

/**
 * The day in the default company time zone (America/New_York), as YYYY-MM-DD. For tests' and
 * fixtures' dates only: the app reads the company's own zone (server/core/week.ts, brief 1E-8).
 */
export const newYorkDate = (now: Date): string => zonedDay(now, DEFAULT_TIME_ZONE);

/**
 * A grant gives only what it lists, only for its record, and only until it ends (the end date
 * is the last day). Returns the grant that allows it, or null.
 */
export function grantFor<G extends Grant>(
  grants: readonly G[],
  kind: RecordKind,
  recordId: string,
  what: string,
  today: string,
): G | null {
  return (
    grants.find(
      (g) =>
        g.record_kind === kind &&
        g.record_id === recordId &&
        g.can_see.includes(what) &&
        today <= g.ends_on,
    ) ?? null
  );
}

/** Checks what a new grant lists. Returns why it's refused, or null. */
export function refuseGrant(grant: Grant): string | null {
  const allowed = GRANT_CAN_SEE[grant.record_kind as RecordKind] as readonly string[] | undefined;
  if (!allowed) return "unknown kind of record";
  if (grant.can_see.length === 0) return "a grant lists what they can see";
  const bad = grant.can_see.find((c) => !allowed.includes(c));
  if (bad) return `a ${grant.record_kind} grant can't show ${bad}`;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(grant.ends_on)) return "a grant needs an end date";
  return null;
}
